import { Award, HeartHandshake, Sparkles, ArrowUpRight } from "lucide-react";
import { whatsappLink } from "@/lib/clinic";

const HIGHLIGHTS = [
  {
    icon: Award,
    title: "Expert Care",
    body: "Specialised in Orthopaedic, Neurological & Sports rehabilitation.",
  },
  {
    icon: HeartHandshake,
    title: "Patient-centric",
    body: "Personalised treatment plans tailored to your unique needs and goals.",
  },
  {
    icon: Sparkles,
    title: "Compassionate",
    body: "Focused on long-term recovery, wellness, and empowering every patient.",
  },
];

export default function Founder() {
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="relative py-16 md:py-32 lg:py-40 bg-sand-50"
    >
      <div className="pointer-events-none absolute -top-32 right-0 w-[420px] h-[420px] ripple-deco opacity-50" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-12 gap-10 md:gap-14 items-center">
          {/* Poster image */}
          <div className="col-span-12 lg:col-span-6 order-2 lg:order-1">
            <figure className="relative">
              <div className="relative overflow-hidden rounded-[2rem] aspect-square shadow-[0_30px_80px_-30px_rgba(7,48,61,0.35)]">
                <img
                  src="/founder.jpg"
                  alt="Dr. Lakshmi — Founder & Chief Physiotherapist at Revive Ripple"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-[1.02]"
                  loading="lazy"
                />
              </div>
              <div className="hidden md:block absolute -bottom-6 -left-6">
                <div className="rounded-2xl bg-forest-900 text-sand-50 px-5 py-4 shadow-[0_18px_40px_-16px_rgba(7,48,61,0.45)]">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-sky-300/90">
                    Licence
                  </p>
                  <p className="font-serif text-xl mt-1">L55072</p>
                </div>
              </div>
            </figure>
          </div>

          {/* Bio */}
          <div className="col-span-12 lg:col-span-6 order-1 lg:order-2">
            <span className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-forest-700">
              <span className="w-10 h-px bg-terracotta-500" />
              Meet our founder
            </span>

            <h2 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-forest-900">
              Dr. <em className="text-terracotta-500">Lakshmi</em>
            </h2>
            <p className="mt-3 text-sm tracking-[0.18em] uppercase text-forest-700/75">
              BPT · MIAP (L55072) · Founder &amp; Chief Physiotherapist
            </p>

            <p className="mt-7 text-lg leading-relaxed text-forest-700/85 max-w-xl">
              A passionate and dedicated physiotherapist committed to helping
              you <span className="text-forest-900 font-medium">move better,
              feel stronger</span> and live pain-free. Every plan she designs is
              built around your story, your pace and your long-term wellbeing.
            </p>

            <div className="mt-10 space-y-5">
              {HIGHLIGHTS.map((h) => {
                const Icon = h.icon;
                return (
                  <div
                    key={h.title}
                    data-testid={`founder-highlight-${h.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex items-start gap-5"
                  >
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-sand-200 text-terracotta-500">
                      <Icon size={20} strokeWidth={1.5} />
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl text-forest-900 leading-snug">
                        {h.title}
                      </h3>
                      <p className="mt-1 text-base text-forest-700/85 leading-relaxed max-w-md">
                        {h.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="founder-cta-btn"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-sand-50 pl-7 pr-5 py-4 text-sm tracking-wide transition-all hover:-translate-y-1 group"
            >
              Book with Dr. Lakshmi
              <span className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-full bg-sand-50 text-terracotta-500 transition-transform group-hover:rotate-45">
                <ArrowUpRight size={16} />
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
